import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Crown, Gift } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { BRAND } from "@/brand.config";

const formatMoney = (value) => `₹${new Intl.NumberFormat("en-IN").format(Number(value || 0))}`;

export default function Home() {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", city: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [cms, setCms] = useState(null);
  const [loyalty, setLoyalty] = useState(null);

  useEffect(() => {
    document.title = BRAND.meta.title;
    api.get("/cms/content").then((r) => setCms(r.data)).catch(() => {});
    api.get("/public/loyalty-config").then((r) => setLoyalty(r.data)).catch(() => {});
    const m = document.querySelector('meta[name="description"]');
    const desc = BRAND.meta.description;
    if (m) m.setAttribute("content", desc);
    else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content = desc;
      document.head.appendChild(meta);
    }
  }, []);

  const c = cms?.home || {};
  const tiers = (loyalty?.tier_rules || []).filter((tier) => tier.is_active !== false);
  const maxBirthdayBonus = Math.max(0, ...tiers.map((tier) => Number(tier.birthday_bonus || 0)));
  const earnHeading = loyalty?.earn_mode === "percent_of_spend"
    ? `Earn ${Number(loyalty.percent_of_spend || 0)}% back in points`
    : loyalty?.earn_ratio
      ? `Earn ${Number(loyalty.earn_ratio)} point${Number(loyalty.earn_ratio) === 1 ? "" : "s"} per ₹1`
      : "Earn on eligible purchases";
  const redeemCopy = loyalty?.burn_ratio
    ? `Each point is worth ${formatMoney(loyalty.burn_ratio)} when you redeem.`
    : "Redemption value and eligibility are managed through the active loyalty rules.";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.mobile || form.mobile.length < 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/public/register-interest", form);
      setResult(r.data);
      if (r.data.already_registered) {
        toast.success(`Welcome back! You're a ${r.data.tier?.toUpperCase()} member with ${r.data.points} points.`);
      } else {
        toast.success(r.data.welcome_bonus > 0 ? `Welcome to Odhni Rewards. ${r.data.welcome_bonus} points credited.` : BRAND.welcomeToast);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative kazo-bg-black text-white overflow-hidden">
        <div className="grid lg:grid-cols-2 min-h-[80vh]">
          <div className="relative order-2 lg:order-1 px-6 lg:px-16 py-16 lg:py-24 flex flex-col justify-center">
            <div className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-6 fade-up">{c.hero_eyebrow || BRAND.homeCopy.heroEyebrow}</div>
            <h1 className="editorial-headline text-5xl md:text-6xl lg:text-7xl xl:text-8xl mb-6 fade-up" style={{ animationDelay: "0.1s" }}>
              {c.hero_headline_1 || "Where style"}<br />
              <em className="kazo-text-champagne font-light">{c.hero_headline_em || "earns"}</em> {c.hero_headline_2?.split(" ")[0] || "you"}<br />
              {c.hero_headline_2?.split(" ").slice(1).join(" ") || "more."}
            </h1>
            <p className="text-white/70 text-lg max-w-md mb-10 leading-relaxed fade-up" style={{ animationDelay: "0.2s" }}>
              {c.hero_subtext || BRAND.homeCopy.heroSubtext}
            </p>

            <form onSubmit={submit} className="space-y-3 max-w-md fade-up" style={{ animationDelay: "0.3s" }}>
              <div className="grid grid-cols-2 gap-3">
                <input style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.2)",color:"#fff"}} className="px-3 py-2.5 text-sm placeholder:text-white/40 rounded-[2px] focus:outline-none focus:border-white/60" placeholder="Full name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="hero-input-name" />
                <input style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.2)",color:"#fff"}} className="px-3 py-2.5 text-sm placeholder:text-white/40 rounded-[2px] focus:outline-none focus:border-white/60" placeholder="Mobile number" value={form.mobile} onChange={(e)=>setForm({...form,mobile:e.target.value})} data-testid="hero-input-mobile" />
              </div>
              <input style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.2)",color:"#fff"}} className="w-full px-3 py-2.5 text-sm placeholder:text-white/40 rounded-[2px] focus:outline-none focus:border-white/60" placeholder="Email (optional)" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="hero-input-email" />
              <input style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.2)",color:"#fff"}} className="w-full px-3 py-2.5 text-sm placeholder:text-white/40 rounded-[2px] focus:outline-none focus:border-white/60" placeholder="City" value={form.city} onChange={(e)=>setForm({...form,city:e.target.value})} data-testid="hero-input-city" />
              <button type="submit" disabled={submitting} className="k-btn kazo-bg-champagne text-black w-full justify-center font-semibold text-sm uppercase tracking-widest" data-testid="hero-submit-btn">
                {submitting ? "Joining…" : BRAND.ctaJoinFree} <ArrowUpRight className="w-4 h-4" />
              </button>
            </form>
            {result && (
              <div className="mt-4 text-sm text-champagne kazo-text-champagne fade-up">
                {result.already_registered
                  ? `Welcome back. ${result.tier ? `Tier: ${result.tier.toUpperCase()} · ` : ""}Balance: ${result.points} pts`
                  : result.welcome_bonus > 0 ? `You're in. ${result.welcome_bonus} welcome points credited.` : "You're in. Your Odhni Rewards membership is active."}
              </div>
            )}

            <div className="mt-10 flex flex-wrap items-center gap-7 text-xs text-white/40 fade-up" style={{ animationDelay: "0.4s" }}>
              {BRAND.homeCopy.stats.map((stat, index) => <div key={stat.label} className="flex items-center gap-7"><div><span className="text-2xl font-display text-white">{stat.value}</span><div className="uppercase tracking-widest mt-1">{stat.label}</div></div>{index < BRAND.homeCopy.stats.length - 1 && <div className="h-8 w-px bg-white/20" />}</div>)}
            </div>
          </div>

          <div className="relative order-1 lg:order-2 min-h-[50vh] lg:min-h-0 overflow-hidden">
            <img src={c.hero_image_url || BRAND.assets.hero} alt={BRAND.homeCopy.heroImageAlt} className="w-full h-full object-cover object-center" data-testid="home-hero-image" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-8 left-8 right-8 lg:hidden text-white">
              <div className="text-[11px] uppercase tracking-[0.3em] opacity-60">Spring · Summer 2026</div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO STRIP */}
      <section className="py-12 px-6 lg:px-12 max-w-[1400px] mx-auto border-b border-black/10">
        <div className="grid md:grid-cols-3 gap-12">
          <div className="flex gap-4">
            <Sparkles className="w-6 h-6 kazo-text-burgundy flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-display text-2xl mb-2" data-testid="home-earn-heading">{earnHeading}</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">Across eligible stores and collections, based on the active loyalty rules.</p>
            </div>
          </div>
          <div className="flex gap-4">
            <Crown className="w-6 h-6 kazo-text-burgundy flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-display text-2xl mb-2" data-testid="home-tier-count-heading">{tiers.length ? `${tiers.length} Tiers, One Journey` : "Privileges That Grow With You"}</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">Your benefits follow the tiers and thresholds currently active in Odhni Rewards.</p>
            </div>
          </div>
          <div className="flex gap-4">
            <Gift className="w-6 h-6 kazo-text-burgundy flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-display text-2xl mb-2">Birthday Privileges</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">{maxBirthdayBonus > 0 ? `Eligible members can receive up to ${maxBirthdayBonus} birthday points.` : "Birthday benefits follow the programme rules active for your membership."}</p>
            </div>
          </div>
        </div>
      </section>

      {/* TIERS */}
      {tiers.length > 0 && <section className="py-24 px-6 lg:px-12 max-w-[1400px] mx-auto" data-testid="home-tiers-section">
        <div className="max-w-3xl mb-16">
          <div className="text-[11px] uppercase tracking-[0.3em] kazo-text-burgundy mb-4">THE TIERS</div>
          <h2 className="editorial-headline text-5xl lg:text-7xl">Your journey.<br /><em className="font-light">More</em> privileges.</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiers.map((t, i) => (
            <div key={t.tier || t.name} className="border border-black/10 p-8 bg-white relative overflow-hidden fade-up" style={{ animationDelay: `${i * 0.1}s` }} data-testid={`tier-card-${String(t.tier || t.name).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <span className="pill mb-6 inline-block" style={{ borderColor: t.color || undefined }}>{t.name || t.tier}</span>
              <div className="font-display text-4xl mb-2">{Number(t.earn_multiplier || 1)}x</div>
              <div className="text-xs uppercase tracking-widest text-neutral-500 mb-6">Earn rate</div>
              <div className="space-y-2 text-sm text-neutral-700">
                <div className="flex justify-between border-b border-black/5 pb-1.5"><span className="text-neutral-500">Min. spend</span><span className="font-medium">{formatMoney(t.min_lifetime_spend)}</span></div>
                <div className="flex justify-between border-b border-black/5 pb-1.5"><span className="text-neutral-500">Birthday bonus</span><span className="font-medium">{Number(t.birthday_bonus || 0)} pts</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Point validity</span><span className="font-medium">{t.point_expiry_override_days || loyalty.point_expiry_days} days</span></div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <Link to="/loyalty-benefits" className="editorial-link text-sm font-medium uppercase tracking-widest inline-flex items-center gap-2">Compare all benefits <ArrowUpRight className="w-4 h-4" /></Link>
        </div>
      </section>}

      {/* EDITORIAL SPLIT */}
      <section className="kazo-bg-black text-white py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative aspect-[4/5] overflow-hidden">
            <img src={c.editorial_image_url || BRAND.assets.editorial} alt={BRAND.homeCopy.editorialImageAlt} className="w-full h-full object-cover object-center" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] kazo-text-champagne mb-4">VIP PREVIEW</div>
            <h2 className="editorial-headline text-5xl lg:text-7xl mb-6">Before<br />the world<br /><em className="kazo-text-champagne font-light">sees it.</em></h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-md">
              {c.editorial_body || "Eligible members receive private collection previews and occasion-led privileges, according to their active tier benefits."}
            </p>
            <Link to="/loyalty-benefits" className="k-btn kazo-bg-champagne text-black uppercase tracking-widest text-xs font-semibold" data-testid="cta-vip-learn-more">
              Learn the privileges <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS strip */}
      <section className="py-24 px-6 lg:px-12 max-w-[1400px] mx-auto">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] kazo-text-burgundy mb-4">SIMPLY</div>
            <h2 className="editorial-headline text-5xl lg:text-7xl mb-8">Three steps.<br />A lifetime<br /><em className="font-light">of rewards.</em></h2>
            <Link to="/how-it-works" className="editorial-link uppercase tracking-widest text-sm font-medium inline-flex items-center gap-2">See full guide <ArrowUpRight className="w-4 h-4" /></Link>
          </div>
          <div className="space-y-8">
            {[
              { step: "01", title: "Join free", body: `Sign up at any ${BRAND.name} store or online with your valid mobile number. Any active welcome benefit is applied automatically.` },
              { step: "02", title: "Earn effortlessly", body: "Quote your mobile at checkout. Eligible points accrue automatically under the active programme rules." },
              { step: "03", title: "Redeem your way", body: redeemCopy },
            ].map((s) => (
              <div key={s.step} className="flex gap-8 border-b border-black/10 pb-8 last:border-0">
                <div className="font-display text-5xl kazo-text-champagne" style={{ fontWeight: 300 }}>{s.step}</div>
                <div>
                  <h3 className="font-display text-2xl mb-1">{s.title}</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed max-w-md">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden" style={{ minHeight: 480 }}>
        <img src={c.boutique_image_url || BRAND.assets.boutique} alt={BRAND.homeCopy.boutiqueImageAlt} className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative max-w-3xl mx-auto px-6 py-24 text-center text-white">
          <div className="text-[11px] uppercase tracking-[0.3em] text-white/60 mb-6">JOIN TODAY</div>
          <h2 className="editorial-headline text-5xl lg:text-7xl mb-8">Your wardrobe.<br /><em className="kazo-text-champagne font-light">Rewarded.</em></h2>
          <p className="text-white/80 mb-10 max-w-xl mx-auto">Membership is free. Privileges are forever.</p>
          <Link to="/" className="k-btn kazo-bg-champagne text-black uppercase tracking-widest text-xs font-semibold" data-testid="cta-final-join">
            Become a member <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
